/**
 * Poseidon dashboard — application shell: sidebar, persistent status ribbon and
 * the dashboard-wide data clock.
 *
 * The ribbon and the last-data-timestamp indicator live here, not inside a page,
 * so they are visible on all seven pages and cannot be scrolled away or omitted
 * by a page author (§3.2 A, §13 Phase 1).
 */
import { createContext, useContext } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { RibbonStatus } from '../api/types';
import { API_MODE, BUILD_ID, POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';
import { MOCK_BANNER_BODY, MOCK_BANNER_TITLE } from '../mock/banner';
import { StatusRibbon } from './StatusRibbon';
import { LastDataTimestamp, SocketStatusBadge } from './Staleness';
import { ErrorState, LoadingState } from './primitives';

interface NavEntry {
  to: string;
  label: string;
  spec: string;
}

/** The seven pages of §3.2, in spec order. */
export const NAV_ENTRIES: readonly NavEntry[] = [
  { to: '/', label: 'Live overview', spec: '§3.2 A' },
  { to: '/trades', label: 'Trades & analytics', spec: '§3.2 B' },
  { to: '/market', label: 'Live market & signal monitor', spec: '§3.2 C' },
  { to: '/risk', label: 'Risk center', spec: '§3.2 D' },
  { to: '/research', label: 'Strategy & AI research', spec: '§3.2 E' },
  { to: '/backtests', label: 'Backtests & forward tests', spec: '§3.2 F' },
  { to: '/operations', label: 'Operations', spec: '§3.2 G' },
];

export function Sidebar(): JSX.Element {
  const { session, isMock } = useServices();
  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="sidebar__brand-name">POSEIDON</span>
        <span className="sidebar__brand-sub">operator dashboard</span>
      </div>
      <nav className="sidebar__nav" aria-label="Dashboard pages">
        {NAV_ENTRIES.map((entry) => (
          <NavLink
            key={entry.to}
            to={entry.to}
            end={entry.to === '/'}
            className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
          >
            <span>{entry.label}</span>
            <span className="text-faint" style={{ fontSize: 10 }}>
              {entry.spec}
            </span>
          </NavLink>
        ))}
      </nav>
      <div className="sidebar__footer">
        <div>
          data source: <strong className={isMock ? 'text-warn' : 'text-ok'}>{API_MODE}</strong>
        </div>
        <div>
          operator: <span className="text-mono">{session?.operator ?? '—'}</span>
        </div>
        <div>build {BUILD_ID}</div>
        <div className="text-faint">
          MT5 demo accounts only. Localhost-only. No move to live trading is possible from this UI.
        </div>
      </div>
    </aside>
  );
}

export function MockDataBanner(): JSX.Element {
  return (
    <div className="banner banner--mock" role="status">
      <span className="banner__title">⚠ {MOCK_BANNER_TITLE}</span>
      <span>{MOCK_BANNER_BODY}</span>
    </div>
  );
}

/**
 * The ribbon is fetched once by the shell and shared through context, so a page
 * can read the account mode, halt state and feed ages without a second poll.
 */
const RibbonContext = createContext<RibbonStatus | null>(null);

export function useRibbon(): RibbonStatus | null {
  return useContext(RibbonContext);
}

export function AppShell(): JSX.Element {
  const { api, isMock, sessionError, socket } = useServices();
  const ribbon = useApiData('ribbon', () => api.getRibbon(), { pollMs: POLL_INTERVALS.ribbon, mode: api.mode });

  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        {isMock && <MockDataBanner />}
        {sessionError !== null && (
          <div className="banner banner--error" role="alert">
            <span className="banner__title">operator session</span>
            <span>{sessionError}</span>
          </div>
        )}
        <header className="topbar">
          <LastDataTimestamp />
          <SocketStatusBadge />
          <span className="topbar__spacer" />
          {ribbon.data !== null && (
            <>
              <span className="text-faint" style={{ fontSize: 11.5 }}>
                broker time{' '}
                <span className="text-mono">
                  {new Date(ribbon.data.session.broker_time).toLocaleTimeString(undefined, { hour12: false })}
                </span>{' '}
                ({ribbon.data.session.broker_timezone})
              </span>
              <span className="text-faint" style={{ fontSize: 11.5 }}>
                account <span className="text-mono">{ribbon.data.account.account_number_masked}</span>
              </span>
            </>
          )}
          {socket.missedEvents > 0 && (
            <span className="text-warn" style={{ fontSize: 11.5 }} title="The event stream lost frames; REST snapshots are being refetched.">
              {socket.missedEvents} event(s) missed — resyncing
            </span>
          )}
        </header>
        <main className="content">
          {ribbon.error !== null && ribbon.data === null && (
            <ErrorState
              error={ribbon.error}
              context="The daemon did not return a status ribbon, so MT5, account, execution, session, risk, freshness and DeepSeek state are unknown."
              onRetry={ribbon.refresh}
            />
          )}
          {ribbon.data === null && ribbon.error === null && <LoadingState label="Loading status ribbon…" />}
          {ribbon.data !== null && <StatusRibbon ribbon={ribbon.data} />}
          <RibbonContext.Provider value={ribbon.data}>
            <Outlet />
          </RibbonContext.Provider>
        </main>
      </div>
    </div>
  );
}
